import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { useGetTeamsQuery } from '../../api/client'
import PageMasthead from '../../components/PageMasthead'
import SimpleLoader from '../../components/SimpleLoader'
import { toastError } from '../../components/Notification'
import { isUserAdminState } from '../../store/DomainStore'
import useContentSearchParams from '../../hooks/useContentQueryParams'
import SearchDefinitionStep from './steps/SearchDefinitionStep'
import DatasetReviewStep from './steps/DatasetReviewStep'
import ChatStep from './steps/ChatStep'

const DEFAULT_MODEL = 'gemini-3-pro'

export default function AskArwenPage() {
    const [urlParams, setUrlParams] = useSearchParams()
    const [admin] = useRecoilState(isUserAdminState)

    const { searchParameters, onSearchParameterChange } = useContentSearchParams()

    // Chat session state
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [itemCount, setItemCount] = useState(0)
    const [earliest, setEarliest] = useState<string | null>(null)
    const [latest, setLatest] = useState<string | null>(null)

    // Get current step from URL
    const stepParam = urlParams.get('step')
    const sessionIdParam = urlParams.get('sessionId')
    const currentStepId = stepParam || 'define'

    // Restore session from URL if present
    useEffect(() => {
        if (sessionIdParam && stepParam === 'chat') {
            setSessionId(sessionIdParam)
        }
    }, [sessionIdParam, stepParam])

    // Fetch teams data
    const { data: getTeamsData, loading: getTeamsLoading } = useGetTeamsQuery({
        onError: () => {
            toastError('Error fetching user teams. Please contact support.')
        }
    })

    const hasMoreThanOneTeam = !!(
        getTeamsData?.getTeams?.length && getTeamsData?.getTeams?.length > 1
    )
    const showTeamSearch = admin || hasMoreThanOneTeam

    // Navigation handlers
    const navigateToStep = (stepId: string, additionalParams?: Record<string, string>) => {
        const newParams = new URLSearchParams(urlParams.toString())

        if (stepId === 'define') {
            newParams.delete('step')
            newParams.delete('sessionId')
        } else {
            newParams.set('step', stepId)
        }

        if (additionalParams) {
            Object.entries(additionalParams).forEach(([key, value]) => {
                newParams.set(key, value)
            })
        }

        setUrlParams(newParams)
    }

    const handleDefineNext = () => {
        navigateToStep('review')
    }

    const handleReviewBack = () => {
        navigateToStep('define')
    }

    const handleStartChat = (
        newSessionId: string,
        newItemCount: number,
        newEarliest: string | null,
        newLatest: string | null
    ) => {
        setSessionId(newSessionId)
        setItemCount(newItemCount)
        setEarliest(newEarliest)
        setLatest(newLatest)
        navigateToStep('chat', { sessionId: newSessionId })
    }

    const handleChatBack = () => {
        setSessionId(null)
        setItemCount(0)
        navigateToStep('define')
    }

    // Get the first selected account ID for the API calls
    const accountId = searchParameters.selectedAccountIds[0] ?? 0

    const loading = getTeamsLoading

    if (loading) {
        return <SimpleLoader loading={true} />
    }

    const isChatStep = currentStepId === 'chat' && sessionId

    // When in chat mode, use flex-1 (flex: 1 1 0%) instead of stretchy (flex: 1 1 auto)
    // This allows the container to shrink below content size, enabling internal scrolling
    const containerClasses = isChatStep
        ? 'flex min-h-0 w-full flex-1 flex-col bg-gray-50'
        : 'stretchy w-full bg-gray-50'

    return (
        <div className={containerClasses}>
            <div className="flex-shrink-0">
                <PageMasthead
                    title="Ask Arwen"
                    subtitle="Query your comment data in plain language"
                />
            </div>

            {/* Main content */}
            <main className={`flex flex-col ${isChatStep ? 'min-h-0 flex-1' : 'py-8 px-8'}`}>
                {currentStepId === 'define' && (
                    <SearchDefinitionStep
                        searchParameters={searchParameters}
                        onSearchParameterChange={onSearchParameterChange}
                        showTeamSearch={showTeamSearch}
                        onNext={handleDefineNext}
                    />
                )}

                {currentStepId === 'review' && (
                    <DatasetReviewStep
                        searchParameters={searchParameters}
                        accountId={accountId}
                        selectedModel={DEFAULT_MODEL}
                        onBack={handleReviewBack}
                        onStartChat={handleStartChat}
                    />
                )}

                {isChatStep && (
                    <ChatStep
                        accountId={accountId}
                        sessionId={sessionId}
                        itemCount={itemCount}
                        searchParameters={searchParameters}
                        onBack={handleChatBack}
                        earliest={earliest}
                        latest={latest}
                    />
                )}
            </main>
        </div>
    )
}
